//@ts-expect-error getting error: @pileus-cloud/anodot-frontend-common' has no exported member PopoverContent
import { GenerateIcon, ICONS, Popover, PopoverContent, PopoverTrigger } from '@pileus-cloud/anodot-frontend-common';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import { Search } from '../../components/Search';
import React, { ReactNode, useMemo, useState } from 'react';
import { useCostCentersContext } from '../contexts/costCentersContext';
import { useCostCenters } from 'users/new-user-management/hooks/reactQuery/useCostCenters';

import styles from './CostCentersSelector.module.scss';

type CostCenter = {
  externalId: string;
  id: string;
  name: string;
  parentCcId?: string;
};

type CostCentersSelectorProps = {
  onCostCenterSelected: (costCenterId: string) => void;
  children: ReactNode;
};

interface CostCenterDataTree {
  costCenter: CostCenter;
  children: CostCenterDataTree[];
}

const CostCenterTreeItem = (props: {
  treeNode: CostCenterDataTree;
  depth: number;
  expandedCostCenters: Set<string>;
  onExpand: (costCenterId: string) => void;
  onOptionSelected: (costCenterId: string) => void;
}) => {
  const { treeNode, depth, expandedCostCenters, onExpand, onOptionSelected } = props;

  return (
    <div className={styles.optionWrapper}>
      <div className={styles.optionContent}>
        {Array.from({ length: depth }).map((_, index) => {
          return <GenerateIcon key={index} className={styles.indicator} iconName={ICONS.chevronRight.name} />;
        })}
        <GenerateIcon
          onClick={() => onExpand(treeNode.costCenter.id)}
          className={styles.indicatorLast}
          iconName={expandedCostCenters.has(treeNode.costCenter.id) ? ICONS.chevronDown.name : ICONS.chevronRight.name}
          style={{ opacity: treeNode.children.length > 0 ? 1 : 0 }}
        />
        <div onClick={() => onOptionSelected(treeNode.costCenter.id)} className={styles.option}>
          {treeNode.costCenter.name}
        </div>
      </div>
      {expandedCostCenters.has(treeNode.costCenter.id) && (
        <div className={styles.children}>
          {treeNode.children.map((child) => (
            <CostCenterTreeItem
              key={child.costCenter.id}
              treeNode={child}
              depth={depth + 1}
              expandedCostCenters={expandedCostCenters}
              onExpand={onExpand}
              onOptionSelected={onOptionSelected}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CostCentersSelector: React.FC<CostCentersSelectorProps> = ({ onCostCenterSelected, children }) => {
  const [isCostCentersSelectorOpen, setIsCostCentersSelectorOpen] = useState(false);
  const { isCustomersView, selectedCustomer } = useCostCentersContext();
  const [searchInput, setSearchInput] = useState('');
  const [expandedCostCenters, setExpandedCostCenters] = useState<Set<string>>(new Set());

  const { fetchManagementCostCenters } = useCostCenters();
  const { data: costCentersData = [], isLoading: isCostCentersDataAccessLoading } = fetchManagementCostCenters(
    isCustomersView,
    selectedCustomer,
  ) as { data?: { costCenter: CostCenter }[]; isLoading: boolean };

  const mappedCostCenters: CostCenter[] = useMemo(() => {
    return Array.isArray(costCentersData) ? costCentersData.map((cc) => cc.costCenter) : [];
  }, [costCentersData]);

  const costCenterDataTree: CostCenterDataTree[] = useMemo(() => {
    const matchesSearch = (costCenter: CostCenter, search: string): boolean => {
      if (!search) return true;
      return costCenter.name?.toLowerCase().includes(search.toLowerCase());
    };

    const hasMatchingDescendant = (costCenterId: string, search: string): boolean => {
      if (!search) return false;

      const children = mappedCostCenters?.filter((cc) => cc.parentCcId === costCenterId) || [];

      return children.some((child) => matchesSearch(child, search) || hasMatchingDescendant(child.id, search));
    };

    const getChildrenRecursive = (costCenterId: string | null): CostCenterDataTree[] => {
      return mappedCostCenters
        ?.filter((cc) => cc.parentCcId === costCenterId)
        .filter((cc) => {
          return matchesSearch(cc, searchInput) || hasMatchingDescendant(cc.id, searchInput);
        })
        .map((cc) => ({
          costCenter: cc,
          children: getChildrenRecursive(cc.id),
        }));
    };

    const result = getChildrenRecursive(null);
    return result;
  }, [mappedCostCenters, searchInput]);

  const onOpenChange = () => {
    setIsCostCentersSelectorOpen((prev) => !prev);
  };

  const handleCostCenterSelected = (costCenterId: string) => {
    onCostCenterSelected(costCenterId);
    setIsCostCentersSelectorOpen(false);
  };

  return (
    <Popover open={isCostCentersSelectorOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <div className={styles.popoverWrapper}>
        <PopoverContent side="bottom" className={styles.popoverContent}>
          {isCostCentersDataAccessLoading ? (
            <div>
              <Spinner size={SPINNER_SIZES.SMALL} />
            </div>
          ) : (
            <>
              <Search
                search={searchInput}
                setSearch={setSearchInput}
                overrideStyle={styles.searchInput}
                isDebounce={false}
              />
              <div className={styles.menuContainer}>
                {!costCenterDataTree?.length ? (
                  <div className={styles.emptyState}>No results found</div>
                ) : (
                  costCenterDataTree?.map((treeNode) => (
                    <CostCenterTreeItem
                      key={treeNode.costCenter.id}
                      treeNode={treeNode}
                      depth={0}
                      expandedCostCenters={expandedCostCenters}
                      onOptionSelected={handleCostCenterSelected}
                      onExpand={(costCenterId) => {
                        setExpandedCostCenters((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(costCenterId)) {
                            newSet.delete(costCenterId);
                          } else {
                            newSet.add(costCenterId);
                          }
                          return newSet;
                        });
                      }}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </PopoverContent>
      </div>
    </Popover>
  );
};

export default CostCentersSelector;
